import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, MapPin, Compass } from "lucide-react";
import api, { fmtErr } from "../lib/api";
import Shell from "../components/app/Shell";
import { toast } from "sonner";

export default function Scan() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [phase, setPhase] = useState("idle"); // idle | scanning | done
  const [venues, setVenues] = useState([]);
  const [presence, setPresence] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/presence/me");
      if (data.presence) setPresence(data);
    })();
  }, []);

  const detect = async () => {
    if (!("geolocation" in navigator)) {
      toast.error(t("scan.geoUnsupported"));
      return;
    }
    setPhase("scanning");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const { data } = await api.post("/presence/ping", {
            lat: latitude, lng: longitude,
            mode: "anonymous", vibe_tag: "just vibing",
            radius_m: 150,
            accuracy: pos.coords.accuracy,
          });
          setPresence({ presence: data.presence, venue: data.venue });
          setTimeout(() => {
            nav("/nearby-list");
          }, 600);
        } catch (e) { toast.error(fmtErr(e)); setPhase("idle"); }
      },
      (err) => {
        toast.error(`Location: ${err.message}. Pick a venue manually.`);
        manualList();
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const manualList = async () => {
    setPhase("scanning");
    setTimeout(async () => {
      try {
        const { data } = await api.get("/venues");
        setVenues(data.venues);
        setPhase("done");
      } catch (e) { toast.error(fmtErr(e)); setPhase("idle"); }
    }, 1400);
  };

  const checkInTo = async (venue) => {
    try {
      const { data } = await api.post("/presence/check-in", { venue_id: venue.id, mode: "anonymous", vibe_tag: "just vibing" });
      setPresence({ presence: data.presence, venue: data.venue });
      toast.success(t("scan.checkedInAt",{name:venue.name}));
      nav("/nearby-list");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const checkOut = async () => {
    await api.post("/presence/check-out");
    setPresence(null);
    toast(t("scan.checkedOut"));
  };

  return (
    <Shell>
      <div className="text-center">
        <p className="font-mono-accent text-[#E60000]">{t("scan.step1")}</p>
        <h1 className="font-display text-4xl mt-3 tracking-tighter">{t("scan.title")}</h1>
      </div>

      {presence ? (
        <div className="mt-8 glass rounded-3xl p-6 text-center" data-testid="active-presence">
          <p className="font-mono-accent text-zinc-500">{presence.venue ? t("scan.currentlyAt") : t("scan.streetMode")}</p>
          <h2 className="font-display text-2xl mt-1">{presence.venue?.name || t("scan.onStreets")}</h2>
          <p className="text-zinc-400 text-sm">
            {presence.venue ? `${presence.venue.address} · ${presence.venue.city}` : t("scan.discoverable150")}
          </p>
          <div className="mt-5 flex gap-2 justify-center flex-wrap">
            {presence.venue && (
              <button data-testid="goto-room" onClick={()=>nav("/live-room")} className="px-5 py-3 rounded-full bg-[#E60000] hover:bg-[#FF1A1A] transition font-semibold">{t("scan.enterLiveRoom")}</button>
            )}
            <button data-testid="goto-nearby" onClick={()=>nav("/nearby-list")} className="px-5 py-3 rounded-full border border-white/15 hover:border-white/40 transition">{t("scan.seeNearby")}</button>
          </div>
          <button onClick={checkOut} className="mt-4 text-sm text-zinc-500 hover:text-white" data-testid="checkout-btn">{t("scan.goInvisible")}</button>
        </div>
      ) : (
        <div className="mt-12 relative h-64 flex items-center justify-center">
          {phase === "scanning" && (
            <>
              <div className="radar-ring" />
              <div className="radar-ring" style={{ animationDelay: "0.6s" }} />
              <div className="radar-ring" style={{ animationDelay: "1.2s" }} />
            </>
          )}
          <div className="relative w-28 h-28 rounded-full bg-[#0a0a0a] border border-[#E60000]/40 flex items-center justify-center shadow-[0_0_60px_rgba(230,0,0,0.4)]">
            <Eye size={48} className="text-[#E60000]" strokeWidth={1.2} />
          </div>
        </div>
      )}

      {!presence && phase === "idle" && (
        <div className="mt-4 space-y-3">
          <button data-testid="detect-btn" onClick={detect} className="w-full py-5 rounded-full bg-[#E60000] hover:bg-[#FF1A1A] hover:shadow-[0_0_30px_rgba(230,0,0,0.5)] transition font-bold text-lg tracking-wide flex items-center justify-center gap-2">
            <Compass size={20} /> {t("scan.detectDiscover")}
          </button>
          <button data-testid="manual-btn" onClick={manualList} className="w-full py-4 rounded-full border border-white/15 hover:border-white/40 transition text-sm">
            {t("scan.pickVenue")}
          </button>
          <p className="text-zinc-500 text-xs text-center mt-2 font-mono-accent">{t("scan.coordsEphemeral")}</p>
        </div>
      )}

      {!presence && phase === "scanning" && (
        <p className="text-center text-zinc-400 mt-8 font-mono-accent" data-testid="scanning-text">{t("scan.scanning")}</p>
      )}

      {!presence && phase === "done" && (
        <div className="mt-8 space-y-3" data-testid="venue-results">
          <p className="font-mono-accent text-zinc-500">{t("scan.verifiedVenues")}</p>
          {venues.length === 0 && <p className="text-zinc-400 text-sm">{t("scan.noVenues")}</p>}
          {venues.map(v => (
            <button key={v.id} data-testid={`venue-${v.id}`} onClick={()=>checkInTo(v)} className="w-full text-left glass rounded-2xl p-4 hover:border-[#E60000]/40 border border-transparent transition">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold">{v.name}</div>
                  <div className="text-zinc-500 text-sm flex items-center gap-1 mt-1"><MapPin size={12} /> {v.city}</div>
                </div>
                <span className="font-mono-accent text-[#E60000]">{t("scan.checkIn")}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </Shell>
  );
}
